import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Shield, Eye, Clock, AlertCircle } from 'lucide-react';

interface AccessLevelBadgeProps {
  clientId: string;
}

export function AccessLevelBadge({ clientId }: AccessLevelBadgeProps) {
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  const { data: grant, isLoading } = useQuery({
    queryKey: ['access-grant', clientId],
    queryFn: () => api.getCurrentAccessGrant(clientId),
    refetchInterval: 60000, // Refresh every minute for countdown
    enabled: !!clientId,
  });

  // Update countdown timer every second
  useEffect(() => {
    if (!grant?.time_remaining_seconds || grant.time_remaining_seconds <= 0) {
      setTimeRemaining('');
      return;
    }

    const updateCountdown = () => {
      if (!grant.time_remaining_seconds) return;

      const seconds = grant.time_remaining_seconds;
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${secs}s`);
      } else {
        setTimeRemaining(`${secs}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [grant?.time_remaining_seconds]);

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-lg border border-slate-700 animate-pulse"
        role="status"
        aria-label="Loading access level information"
      >
        <div className="h-4 w-4 bg-slate-700 rounded" />
        <div className="h-4 w-24 bg-slate-700 rounded" />
        <span className="sr-only">Loading access level...</span>
      </div>
    );
  }

  if (!grant || grant.access_level === 'none') {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-lg border border-slate-700"
        role="status"
        aria-label="No access to this client"
      >
        <AlertCircle className="h-4 w-4 text-slate-500" aria-hidden="true" />
        <span className="text-sm font-medium text-slate-500">No Access</span>
      </div>
    );
  }

  // Admin Temporary Access (elevated)
  if (grant.is_admin && grant.access_level === 'admin_temporary') {
    const isExpiringSoon = grant.time_remaining_seconds && grant.time_remaining_seconds < 3600; // <1 hour

    const ariaLabel = timeRemaining
      ? `Admin access expires in ${timeRemaining}${isExpiringSoon ? ' - expiring soon' : ''}`
      : 'Admin access active';

    return (
      <div
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
          isExpiringSoon
            ? 'bg-red-900/30 border-red-700 text-red-300'
            : 'bg-red-900/20 border-red-800 text-red-400'
        }`}
        role="status"
        aria-label={ariaLabel}
        aria-live="polite"
      >
        <Shield className={`h-4 w-4 ${isExpiringSoon ? 'animate-pulse' : ''}`} aria-hidden="true" />
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium">Admin Access</span>
          {timeRemaining && (
            <>
              <span className="text-slate-500" aria-hidden="true">•</span>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" aria-hidden="true" />
                <span className="text-xs font-mono" aria-label={`Expires in ${timeRemaining}`}>
                  {timeRemaining}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Support Read-Only Access
  if (grant.access_level === 'support_readonly') {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 bg-emerald-900/20 rounded-lg border border-emerald-800"
        role="status"
        aria-label="Support access - read-only permissions"
      >
        <Eye className="h-4 w-4 text-emerald-500" aria-hidden="true" />
        <span className="text-sm font-medium text-emerald-400">Support Access</span>
      </div>
    );
  }

  // Support With Approval
  if (grant.access_level === 'support_with_approval') {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 bg-blue-900/20 rounded-lg border border-blue-800"
        role="status"
        aria-label="Support access - approval required for changes"
      >
        <Eye className="h-4 w-4 text-blue-500" aria-hidden="true" />
        <span className="text-sm font-medium text-blue-400">Support (Approval Required)</span>
      </div>
    );
  }

  // Full Access (permanent)
  if (grant.access_level === 'full') {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 bg-purple-900/20 rounded-lg border border-purple-800"
        role="status"
        aria-label="Full access - all permissions granted"
      >
        <Shield className="h-4 w-4 text-purple-500" aria-hidden="true" />
        <span className="text-sm font-medium text-purple-400">Full Access</span>
      </div>
    );
  }

  // Fallback for unknown access levels
  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-lg border border-slate-700"
      role="status"
      aria-label={`Access level: ${grant.access_level}`}
    >
      <AlertCircle className="h-4 w-4 text-slate-500" aria-hidden="true" />
      <span className="text-sm font-medium text-slate-400">{grant.access_level}</span>
    </div>
  );
}
